/**
 * WP7b / #46616 — terminal + stage for the verification shell.
 *
 * HG6: Terminal is local/operator only by default. Not a VerificationTenantSurface
 * (HG11) — do not mint a new capability. Chat entry stays WP7a; do not import
 * tree.ts from here.
 *
 * Tools-menu Terminal toggles an in-shell dock (`excellent:toggle-terminal`)
 * without navigating away. Stage is a constrained main-column overlay that
 * must not cover the top bar or critical review actions.
 */

export const OPEN_TERMINAL_EVENT = "excellent:open-terminal";
export const CLOSE_TERMINAL_EVENT = "excellent:close-terminal";
export const TOGGLE_TERMINAL_EVENT = "excellent:toggle-terminal";
export const OPEN_STAGE_EVENT = "excellent:open-verification-stage";
export const CLOSE_STAGE_EVENT = "excellent:close-verification-stage";

export const TERMINAL_OPERATOR_ROLES = ["operator", "owner", "admin"] as const;
export const TERMINAL_DENIED_ROLES = [
  "reviewer",
  "verifier",
  "auditor",
  "external-reviewer",
] as const;

export const TERMINAL_DISABLED_REASON = "Terminal is local/operator only";
export const TERMINAL_REMOTE_REASON = "Terminal is local only — not available on a remote session";
export const TERMINAL_CONTEXT_REASON =
  "Terminal is unavailable until Environment context is current";

export type TerminalAccess = { allowed: true; reason: null } | { allowed: false; reason: string };

export type TerminalActivation =
  { type: "event"; name: typeof TOGGLE_TERMINAL_EVENT } | { type: "blocked"; reason: string };

/**
 * Stage lives in the main column under the top bar — never a full-viewport
 * scrim. Review accept/reject in the page header stay clickable.
 */
export const STAGE_OVERLAY_PLACEMENT = {
  region: "main-bottom",
  position: "relative",
  coversReviewActions: false,
  coversTopbar: false,
} as const;

export function normalizeRoleLabel(roleLabel: string): string {
  return roleLabel.trim().toLowerCase().replace(/\s+/g, " ");
}

export function isOperatorRole(roleLabel: string): boolean {
  const n = normalizeRoleLabel(roleLabel);
  if (n === "local operator") return true;
  return (TERMINAL_OPERATOR_ROLES as readonly string[]).includes(n);
}

export function isDeniedRole(roleLabel: string): boolean {
  return (TERMINAL_DENIED_ROLES as readonly string[]).includes(normalizeRoleLabel(roleLabel));
}

export function isLocalOperatorHost(hostname: string): boolean {
  const h = hostname.trim().toLowerCase();
  return h === "localhost" || h === "127.0.0.1" || h === "::1" || h === "[::1]";
}

export function resolveTerminalAccess(input: {
  roleLabel: string;
  hostname?: string;
  locality?: "local" | "remote" | "offline-snapshot" | "unknown";
}): TerminalAccess {
  if (input.locality !== undefined) {
    if (input.locality !== "local") {
      return { allowed: false, reason: TERMINAL_REMOTE_REASON };
    }
  } else if (!input.hostname || !isLocalOperatorHost(input.hostname)) {
    return { allowed: false, reason: TERMINAL_REMOTE_REASON };
  }
  if (!isOperatorRole(input.roleLabel)) {
    return { allowed: false, reason: TERMINAL_DISABLED_REASON };
  }
  return { allowed: true, reason: null };
}

export function activateTerminal(access: TerminalAccess): TerminalActivation {
  if (!access.allowed) return { type: "blocked", reason: access.reason };
  return { type: "event", name: TOGGLE_TERMINAL_EVENT };
}

export function terminalEntryTitle(access: TerminalAccess): string {
  return access.allowed ? "Terminal" : access.reason;
}

function dispatch(name: string): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(name));
}

export function dispatchToggleTerminal(): void {
  dispatch(TOGGLE_TERMINAL_EVENT);
}

export function dispatchOpenTerminal(): void {
  dispatch(OPEN_TERMINAL_EVENT);
}

export function dispatchCloseTerminal(): void {
  dispatch(CLOSE_TERMINAL_EVENT);
}

export function dispatchOpenStage(): void {
  dispatch(OPEN_STAGE_EVENT);
}

export function dispatchCloseStage(): void {
  dispatch(CLOSE_STAGE_EVENT);
}

export function activateTerminalInWindow(access: TerminalAccess): void {
  const next = activateTerminal(access);
  if (next.type === "event") dispatchToggleTerminal();
}
